import {
  faArrowsRotate,
  faCheck,
  faCopy,
  faEye,
  faEyeSlash,
  faKey,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Button,
  Grid,
  Icon,
  Inline,
  Message,
  Modal,
  Section,
  Tooltip,
} from "@intility/bifrost-react";
import { useState } from "react";
import {
  useClient,
  useGetSecret,
  useRevealSecret,
  useRotateSecret,
} from "../api/clients";
import type { ClientListItem } from "../api/types";
import { useCanManageClient } from "./useCanEditClient";

// The client secret of a client that authenticates with one, in the place a
// SPIFFE client has its workload identity. Owner-only, as on the client page.
export function ClientSecret({ client }: { client: ClientListItem }) {
  const { data: detail } = useClient(client.id);
  const canManage = useCanManageClient(client);
  if (detail?.authMethod !== "secret" || !canManage) return null;
  return (
    <Section
      border
      radius
      background="base-3"
      className="client-object-workload"
    >
      <Section.Header>
        <Inline align="center">
          <Icon icon={faKey} />
          <Inline.Stretch>Client secret</Inline.Stretch>
        </Inline>
      </Section.Header>
      <Section.Content className="client-object-workload-body">
        <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
          Use the client ID and secret for client_credentials token requests.
          <br />
          Treat the secret like a password: anyone with it can authenticate as
          this client.
        </p>
        <SecretField secretClientId={client.id} name={client.name} />
      </Section.Content>
    </Section>
  );
}

// A secret in a box: masked, with buttons to copy it, reveal it and rotate it.
// `secretClientId` is the Keycloak id of the client that holds the secret: the
// client itself, or its local development companion.
export function SecretField({
  secretClientId,
  name,
}: {
  secretClientId: string;
  /** Shown in the rotate confirmation. */
  name: string;
}) {
  const getSecret = useGetSecret(secretClientId);
  const reveal = useRevealSecret(secretClientId);
  const rotate = useRotateSecret(secretClientId);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);

  // The full secret is fetched only when the user copies or reveals it.
  const fullSecret = reveal.data?.secret ?? null;
  const masked = getSecret.data?.maskedSecret ?? "";
  const shown = revealed && fullSecret ? fullSecret : masked;

  async function copy() {
    try {
      const secret = fullSecret ?? (await reveal.mutateAsync()).secret;
      if (secret) {
        await navigator.clipboard.writeText(secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // The error shows from the reveal state below.
    }
  }

  async function toggleReveal() {
    try {
      if (!revealed && !fullSecret) await reveal.mutateAsync();
      setRevealed((r) => !r);
    } catch {
      // The error shows from the reveal state below.
    }
  }

  async function confirmRotate() {
    try {
      await rotate.mutateAsync();
      reveal.reset();
      setRevealed(false);
      setRotating(false);
    } catch {
      // The error shows in the dialog.
    }
  }

  function closeRotate() {
    if (rotate.isPending) return;
    setRotating(false);
    rotate.reset();
  }

  return (
    <>
      {getSecret.isLoading && (
        <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
          Loading…
        </p>
      )}
      {getSecret.data && (
        <div className="client-api-node">
          <code
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: "monospace",
              wordBreak: "break-all",
            }}
          >
            {shown}
          </code>
          <Tooltip content={copied ? "Copied" : "Copy secret"}>
            <Button
              variant="flat"
              small
              className="client-icon-button"
              aria-label="Copy secret"
              disabled={reveal.isPending}
              onClick={copy}
            >
              <Icon icon={copied ? faCheck : faCopy} />
            </Button>
          </Tooltip>
          <Tooltip content={revealed ? "Hide secret" : "Reveal secret"}>
            <Button
              variant="flat"
              small
              className="client-icon-button"
              aria-label={revealed ? "Hide secret" : "Reveal secret"}
              disabled={reveal.isPending}
              onClick={toggleReveal}
            >
              <Icon icon={revealed ? faEyeSlash : faEye} />
            </Button>
          </Tooltip>
          <Tooltip content="Rotate secret">
            <Button
              variant="flat"
              state="alert"
              small
              className="client-api-revoke"
              aria-label="Rotate secret"
              onClick={() => setRotating(true)}
            >
              <Icon icon={faArrowsRotate} />
            </Button>
          </Tooltip>
        </div>
      )}

      {reveal.error && (
        <Message state="warning" header="Reveal failed">
          {reveal.error instanceof Error
            ? reveal.error.message
            : "An unexpected error occurred."}
        </Message>
      )}
      {getSecret.error && (
        <Message state="warning" header="Failed to load">
          {getSecret.error instanceof Error
            ? getSecret.error.message
            : "An unexpected error occurred."}
        </Message>
      )}

      <Modal
        isOpen={rotating}
        onRequestClose={closeRotate}
        header="Rotate client secret"
        width={480}
      >
        <Grid gap={16}>
          <p className="bf-p" style={{ margin: 0 }}>
            This generates a new secret for <strong>{name}</strong>. The current
            secret stops working immediately, so every service using it must be
            updated. This cannot be undone.
          </p>
          {rotate.error && (
            <Message state="alert" header="Rotate failed">
              {rotate.error instanceof Error
                ? rotate.error.message
                : "Something went wrong."}
            </Message>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button
              variant="flat"
              onClick={closeRotate}
              state={rotate.isPending ? "inactive" : undefined}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              state={rotate.isPending ? "inactive" : "alert"}
              onClick={rotate.isPending ? undefined : confirmRotate}
            >
              {rotate.isPending ? "Rotating…" : "Rotate secret"}
            </Button>
          </div>
        </Grid>
      </Modal>
    </>
  );
}
